import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import i18next from 'i18next';
import { Assets, BitmapText, Container, Graphics, LineJoin, Point, Sprite, Text } from 'pixi.js';
import { GameScene } from '..';
import { DEBUG_CONFIG, SHARED_DEFAULT_STYLE_NAME } from './TextContainer.constants';
import { TextContainerConfig, TextContainerToken } from './TextContainer.types';

export class TextContainer extends Container {
    public scene: GameScene;
    private static readonly measureCache = new Map<string, { width: number; height: number }>();
    private readonly sharedDefaultConfig: TextContainerConfig;
    public readonly config: TextContainerConfig;
    private text: string;
    private tokens: TextContainerToken[] = [];
    private lineHeight: number;
    private containerWidth: number;
    private containerHeight: number;
    private measureText!: Text;
    private readonly useBitmapText: boolean;
    private lineWidths: number[] = [];
    public renderScaleX: number = 1;
    public renderScaleY: number = 1;
    private layoutCount = 0;

    public static getTextConfig(textId: string): TextContainerConfig {
        const { defaultStyle: defaultStyleName, styles, texts } = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS);
        const defaultStyle = defaultStyleName && styles?.[defaultStyleName];
        const sharedDefaultConfig = styles?.[SHARED_DEFAULT_STYLE_NAME];
        const styleBinding = texts?.[textId];
        let baseConfig, overrideConfig: Partial<TextContainerConfig> | undefined;
        if (styleBinding?.style) baseConfig = styleBinding.style && styles?.[styleBinding.style];
        else baseConfig = defaultStyle;
        if (styleBinding?.styleOverrides) overrideConfig = styleBinding.styleOverrides;
        const config = { ...sharedDefaultConfig, ...baseConfig, ...overrideConfig } as TextContainerConfig;
        return config;
    }

    public static fromSlot(slotName: string, scene: GameScene, text?: string): TextContainer {
        const config = TextContainer.getTextConfig(slotName);
        const finalText = text ?? i18next.t(slotName);
        return new TextContainer(finalText, scene, config);
    }

    constructor(text: string, scene: GameScene, config?: Partial<TextContainerConfig>) {
        super({ parent: scene.stage });
        this.scene = scene;
        this.sharedDefaultConfig = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS).styles?.[SHARED_DEFAULT_STYLE_NAME] as TextContainerConfig;
        this.text = text;
        this.config = {
            ...this.sharedDefaultConfig,
            ...config
        };

        this.useBitmapText = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS).bitmapFonts.includes(this.config.fontFamily);

        this.containerWidth = this.config.width;
        this.containerHeight = this.config.height;
        if (this.config.debug) this.drawDebugRect();
        this.updateLayout();
        this.zIndex = this.config.zIndex;
        this.scene.stage.addChild(this);
    }

    private get scaledWidth(): number {
        return this.containerWidth * this.renderScaleX;
    }

    private get scaledHeight(): number {
        return this.containerHeight * this.renderScaleY;
    }

    public setText(text: string): void {
        if (this.text === text) return;
        this.text = text;
        this.updateLayout();
    }

    public getText(): string {
        return this.text;
    }

    public setWidth(width: number) {
        this.containerWidth = width;
        this.updateLayout();
    }

    public setHeight(height: number) {
        this.containerHeight = height;
        this.updateLayout();
    }

    public setRenderScale(scaleX: number, scaleY: number): void {
        if (this.renderScaleX === scaleX && this.renderScaleY === scaleY) return;
        this.renderScaleX = scaleX;
        this.renderScaleY = scaleY;
        this.updateLayout();
    }

    public setColor(color: string): void {
        this.children.forEach((child: any) => {
            if (child.setTint) {
                if (child.tint && child.tint === color) return;
                child.setTint(color);
            }
        });
    }

    private measureWord(value: string, fontFamily: string, fontSize: number, isBitmap: boolean): { width: number; height: number } {
        const key = `${value}|${fontFamily}|${fontSize}|${isBitmap}`;

        if (TextContainer.measureCache.has(key)) {
            return TextContainer.measureCache.get(key)!;
        }

        let width: number;
        let height: number;

        if (isBitmap) {
            const bmp = new BitmapText({
                text: value,
                style: {
                    fontFamily,
                    fontSize
                }
            });
            const bounds = bmp.getLocalBounds();

            bmp.destroy();
            width = bounds.width;
            height = bounds.height;
        } else {
            this.measureText.style = {
                fontFamily,
                fontSize
            };
            this.measureText.text = value;
            const bounds = this.measureText.getLocalBounds();
            width = bounds.width;
            height = bounds.height;
            this.measureText.style = {
                fontFamily: this.config.fontFamily,
                fontSize
            };
        }
        this.layoutCount++;

        const result = { width, height };
        TextContainer.measureCache.set(key, result);
        return result;
    }

    private effectiveStyle(styleOverride?: Partial<TextContainerConfig>): {
        fontFamily: string;
        fontSize: number;
        color: string;
        strokeColor: string;
        strokeThickness: number;
        strokeJoin: LineJoin;
        isBitmap: boolean;
    } {
        const fontFamily = styleOverride?.fontFamily ?? this.config.fontFamily;
        const fontSize = styleOverride?.fontMaxSize ?? this.config.fontMaxSize;
        const color = styleOverride?.color ?? this.config.color;
        const strokeColor = styleOverride?.strokeColor ?? this.config.strokeColor;
        const strokeThickness = styleOverride?.strokeThickness ?? this.config.strokeThickness;
        const isBitmap = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS).bitmapFonts.includes(fontFamily);
        const strokeJoin = styleOverride?.strokeJoin ?? this.config.strokeJoin;

        return { fontFamily, fontSize, color, strokeColor, strokeThickness, isBitmap, strokeJoin };
    }

    private updateLayout(): void {
        this.measureText = new Text({
            text: '',
            style: {
                fontFamily: this.config.fontFamily,
                fontSize: this.config.fontMaxSize,
                fill: this.config.color,
                stroke: { color: this.config.strokeColor, width: this.config.strokeThickness, join: this.config.strokeJoin }
            }
        });
        this.measureText.visible = false;
        const fontScale = Math.min(this.renderScaleX, this.renderScaleY);
        const minSize = Math.max(1, Math.round(this.config.fontMinSize * fontScale));
        const maxSize = Math.max(1, Math.round(this.config.fontMaxSize * fontScale));
        const maxSizeClamped = Math.min(this.scaledHeight, maxSize);
        const bestFontSize = this.binarySearch(minSize, maxSizeClamped, (size) => this.layoutTokens(size));
        this.layoutTokens(bestFontSize);
        this.centerTokens();
        this.renderTokens(bestFontSize);
        this.measureText.destroy();
    }

    private tokenize(scene: GameScene, input: string, fontSize: number): TextContainerToken[] {
        const tokens: TextContainerToken[] = [];

        const regex = /\[\/style\]|\[style:([^\]]+)\]|\[img:(.+?)\]|[^\s\n\[]+|\n/g;
        let match: RegExpExecArray | null;

        let currentStyleOverride: Partial<TextContainerConfig> | undefined = undefined;

        while ((match = regex.exec(input)) !== null) {
            if (match[1] !== undefined) {
                const { styles } = ConfigLoader.getConfiguration(EConfigurationNames.TEXTS);
                currentStyleOverride = styles?.[match[1]];
                continue;
            }

            if (match[0] === '[/style]') {
                currentStyleOverride = undefined;
                continue;
            }

            if (match[2] !== undefined) {
                const key = match[2];
                const image = Assets.get(key);
                let width = fontSize;
                let height = fontSize;

                if (image) {
                    const naturalWidth = image.getSourceImage().width;
                    const naturalHeight = image.getSourceImage().height;
                    const scale = this.config.imageConfigs?.[key]?.scale ?? this.config.imageScale;
                    let targetHeight = fontSize * scale;
                    targetHeight = Math.min(targetHeight, this.scaledHeight);
                    const aspectRatio = naturalWidth / naturalHeight;
                    width = targetHeight * aspectRatio;
                    height = targetHeight;
                }

                tokens.push({ imageKey: key, width, height });
                continue;
            }

            if (match[0] === '\n') {
                tokens.push({ width: 0, height: 0, isNewline: true });
                continue;
            }

            const value = match[0];
            const eff = this.effectiveStyle(currentStyleOverride);

            const scaledFontSize =
                currentStyleOverride?.fontMaxSize !== undefined
                    ? Math.round(fontSize * (currentStyleOverride.fontMaxSize / this.config.fontMaxSize))
                    : fontSize;

            const { width, height } = this.measureWord(value, eff.fontFamily, scaledFontSize, eff.isBitmap);

            tokens.push({
                value,
                width,
                height,
                styleOverride: currentStyleOverride
            });
        }

        return tokens;
    }

    private layoutTokens(fontSize: number): boolean {
        const tokens = this.tokenize(this.scene, this.text, fontSize);
        let currentX = 0;
        let currentY = 0;
        let lineCount = 0;

        this.lineWidths = [];

        let currentLineWidth = 0;

        const maxTokenHeight = tokens.reduce((max, t) => Math.max(max, t.height), 0);
        this.lineHeight = Math.max(fontSize, maxTokenHeight) * this.config.lineHeightFactor;
        let currentHeight = this.lineHeight;
        const wordSpace = fontSize * this.config.wordSpaceFactor;

        this.tokens = [];

        for (const token of tokens) {
            if (token.isNewline) {
                const trailingWordSpace = currentX === 0 ? 0 : wordSpace;
                this.lineWidths[lineCount] = currentLineWidth - trailingWordSpace;

                currentY += this.lineHeight;
                currentHeight += this.lineHeight;

                currentX = 0;
                currentLineWidth = 0;

                lineCount++;

                if (currentHeight > this.scaledHeight) return false;
                continue;
            }

            if (token.width > this.scaledWidth || this.lineHeight > this.scaledHeight) return false;

            if (currentX + token.width > this.scaledWidth) {
                const trailingWordSpace = currentX === 0 ? 0 : wordSpace;
                this.lineWidths[lineCount] = currentLineWidth - trailingWordSpace;

                if (lineCount >= this.config.maxLines - 1 || currentHeight + this.lineHeight > this.scaledHeight) return false;

                lineCount++;

                currentY += this.lineHeight;
                currentHeight += this.lineHeight;

                currentX = 0;
                currentLineWidth = 0;
            }

            token.x = currentX;
            token.y = currentY;
            token.line = lineCount;
            this.tokens.push(token);

            currentX += token.width + wordSpace;
            currentLineWidth = currentX;
        }

        const trailingWordSpace = currentX === 0 ? 0 : wordSpace;
        this.lineWidths[lineCount] = currentLineWidth - trailingWordSpace;

        return true;
    }

    private centerTokens(): void {
        if (!this.tokens.length) return;

        const lines = this.groupTokensByLine(this.tokens);

        this.centerTokensVerticallyWithinLine(lines);

        if (this.config.centerHorizontally) this.centerLinesHorizontally(lines);

        if (this.config.centerVertically) this.centerLinesVertically();
    }

    private groupTokensByLine(tokens: TextContainerToken[]): TextContainerToken[][] {
        if (!tokens.length) return [];

        const lines: TextContainerToken[][] = [];
        let currentLine: TextContainerToken[] = [];
        let currentLineIndex = tokens[0].line!;

        for (const token of tokens) {
            if (token.line !== currentLineIndex) {
                lines.push(currentLine);
                currentLine = [];
                currentLineIndex = token.line!;
            }
            currentLine.push(token);
        }

        if (currentLine.length) lines.push(currentLine);
        return lines;
    }

    private centerTokensVerticallyWithinLine(lines: TextContainerToken[][]): void {
        for (const line of lines) {
            for (const token of line) {
                token.y! += (this.lineHeight - token.height) / 2;
            }
        }
    }

    private centerLinesHorizontally(lines: TextContainerToken[][]): void {
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const lineWidth = this.lineWidths[i] ?? 0;
            const offsetX = (this.scaledWidth - lineWidth) * 0.5;
            for (const token of line) {
                token.x! += offsetX;
            }
        }
    }

    private centerLinesVertically(): void {
        const totalHeight = this.tokens.length ? this.lineHeight * this.groupTokensByLine(this.tokens).length : 0;
        const offsetY = (this.scaledHeight - totalHeight) / 2;

        for (const token of this.tokens) {
            token.y! += offsetY;
        }
    }

    private renderTokens(fontSize: number): void {
        this.removeChildren();
        if (this.config.debug) this.drawDebugRect();
        for (let i = 0; i < this.tokens.length; i++) {
            const token = this.tokens[i];
            const offsetX = token.x! - 0.5 * this.scaledWidth;
            const offsetY = token.y! - 0.5 * this.scaledHeight;

            if (token.value) {
                const eff = this.effectiveStyle(token.styleOverride);

                const scaledFontSize =
                    token.styleOverride?.fontMaxSize !== undefined
                        ? Math.round(fontSize * (token.styleOverride.fontMaxSize / this.config.fontMaxSize))
                        : fontSize;

                if (eff.isBitmap) {
                    const bmp = new BitmapText({ text: token.value, style: { fontFamily: eff.fontFamily, fontSize: scaledFontSize } });
                    bmp.position = new Point(offsetX, offsetY);
                    this.addChild(bmp);
                } else {
                    const textObj = new Text({
                        text: token.value,
                        style: {
                            fontFamily: eff.fontFamily,
                            fontSize: scaledFontSize,
                            fill: eff.color,
                            stroke: { color: eff.strokeColor, width: eff.strokeThickness, join: eff.strokeJoin }
                        }
                    });
                    textObj.position = new Point(offsetX, offsetY);

                    this.addChild(textObj);
                }
            } else if (token.imageKey) {
                const img = new Sprite({ texture: Assets.get(token.imageKey) });
                const scaleX = token.width / img.width;
                const scaleY = token.height / img.height;
                img.scale = new Point(scaleX, scaleY);
                img.position = new Point(offsetX + token.width / 2, offsetY + token.height / 2);
                this.addChild(img);
            }

            if (this.config.debug) this.drawTokenDebug(token, i);
        }
    }

    private binarySearch(low: number, high: number, isValid: (mid: number) => boolean): number {
        let result = low;
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            if (isValid(mid)) {
                result = mid;
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }
        return result;
    }

    private drawDebugRect(): void {
        const graphics = new Graphics();
        graphics.rect(-0.5 * this.scaledWidth, -0.5 * this.scaledHeight, this.scaledWidth, this.scaledHeight);
        graphics.fill({ color: DEBUG_CONFIG.FILL_COLOR, alpha: DEBUG_CONFIG.FILL_ALPHA });
        graphics.rect(-0.5 * this.scaledWidth, -0.5 * this.scaledHeight, this.scaledWidth, this.scaledHeight);
        graphics.stroke({ color: DEBUG_CONFIG.LINE_WIDTH, alpha: DEBUG_CONFIG.LINE_COLOR, width: 1 });

        this.addChild(graphics);
    }

    private drawTokenDebug(token: TextContainerToken, index: number): void {
        if (!this.config.debug) return;

        const g = new Graphics();
        const offsetX = token.x! - 0.5 * this.scaledWidth;
        const offsetY = token.y! - 0.5 * this.scaledHeight;

        g.rect(offsetX, offsetY, token.width, token.height);
        g.stroke({ width: 1, color: 0xff0000 });

        const label = new Text({
            text: String(index),
            style: {
                fontSize: '10px',
                fill: '#ff0000'
            }
        });

        label.position = new Point(offsetX, offsetY);

        this.addChild(g);
        this.addChild(label);
    }
}
